import React, { Fragment, useContext, useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { UserContext } from "../UserContext";
import { hasAnyPerm } from '../utils/uiUtils';
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { PencilIcon } from '@primer/octicons-react'
import { BiCheckSquare, BiRectangle } from "react-icons/bi";
//import { ChevronDownIcon, ChevronUpIcon } from '@primer/octicons-react';
import { axiosAuth_PLATFORM_API } from '../utils/AxoisUtilComponent';
import { useTranslation } from 'react-i18next';
import classes from './AdminPages.module.css';
import { translateAPIError } from '../utils/apiUtils';
import { formatDate } from '../utils/timeUtils';


const UserMgmtConfigAdmin = () => {

  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const { t } = useTranslation(['common', 'custom']);

  const [platformConfig,      setPlatformConfig]   = useState({
    config:  {},
    creatorUsername:  '',
    timeCreated:      '',
    updaterUsername:  '',
    timeUpdated:      ''
  });

  const [pwdComplexityAttrs,    setPwdComplexityAttrs] = useState({
    minLength:           12,
    maxLength:           30,
    reqLowerCaseLetter:  false,
    reqUpperCaseLetter:  true,
    reqNumber:           true,
    reqSpecialChar:      false
  });

  const [errMsg_editPwdComplexity, setErrMsg_editPwdComplexity] = useState('');
  //const [viewPwdComplexitySect,    setViewPwdComplexitySect]    = useState(true);
  const [pwdComplexityEditMode,    setPwdComplexityEditMode]    = useState(false);


  // UI Functions

  // Initialize state
  useEffect(() => {

    // Route to home page if not authorized
    if (!hasAnyPerm(user, ["SystemAdminFullAccess", "PlatformAdminWriteAccess"], true)) {
      navigate("/AdminHome/Dashboard", {
        state: {
          //...values
          target: 'Dashboard'
        }
      });
    }

    fetchPlatformConfig();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const fetchPlatformConfig = () => {

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_PLATFORM_API.get("platform-config");

        resolve(res.data);

        if (res.status === 200) {
          setPlatformConfig(res.data[0]);
          setPwdComplexityAttrs(res.data[0].config.public.pwdComplexity.details);
        }
      } catch (error) {
        setErrMsg_editPwdComplexity(t("I18N_ERR_Fail_to_load_pwd_cmplx"));
      }
    });
  }


  // ----- General UI stuff -----

/*
  function handleToggleViewSect(sect,e) {

    switch (sect) {
      case 'PASSWORD_COMPLEXITY':
        setViewPwdComplexitySect(!viewPwdComplexitySect);
        break;
      default:
        break;
    }
  }
*/



  // --- Edit Password Complexity ---

  const onObjAttrsInputChange = (e) => {
    setPwdComplexityAttrs({ ...pwdComplexityAttrs, [e.target.name]: e.target.value })
  }

  const onCheckboxSelectPwdComplexity = (e) => {
    setPwdComplexityAttrs({ ...pwdComplexityAttrs, [e.target.name]: (e.target.checked ? true : false) });
  }

  function handleEditPwdComplexity() {

    setPwdComplexityEditMode('EDIT');
  }

  function handleEditPwdComplexityCancel() {
    setPwdComplexityAttrs(platformConfig.config.public.pwdComplexity.details);
    setPwdComplexityEditMode(false);
    setErrMsg_editPwdComplexity("");
  }

  function handleEditPwdComplexitySubmit() {

    // Validate form inputs
    if (!isNaturalNumber(pwdComplexityAttrs.minLength)) {
      setErrMsg_editPwdComplexity(t("I18N_ERR_Validate_min_len"));
      return;
    } else if (parseInt(pwdComplexityAttrs.minLength) < 12) {
      setErrMsg_editPwdComplexity(t("I18N_ERR_Min_leng_below_limit") + 12);
      return;
    } else if (!isNaturalNumber(pwdComplexityAttrs.maxLength)) {
      setErrMsg_editPwdComplexity(t("I18N_ERR_Validate_max_len"));
      return;
    } else if (parseInt(pwdComplexityAttrs.maxLength) < parseInt(pwdComplexityAttrs.minLength)) {
      setErrMsg_editPwdComplexity(t("I18N_ERR_Min_less_than_max_len"));
      return;
    } else {
      setErrMsg_editPwdComplexity('');

      const reqBody = platformConfig;

      // Compose password complexity regular expression
      let regExp = "^" +
                   (pwdComplexityAttrs.reqNumber          ? "(?=.*[0-9])"        : "") +
                   (pwdComplexityAttrs.reqLowerCaseLetter ? "(?=.*[a-z])"        : "") +
                   (pwdComplexityAttrs.reqUpperCaseLetter ? "(?=.*[A-Z])"        : "") +
                   (pwdComplexityAttrs.reqSpecialChar     ? "(?=.*[!@#$%^&*()_+={\\}[\\]|\\\\:;\"'<>,.?/])" : "") +
                   ".{" + pwdComplexityAttrs.minLength + "," + pwdComplexityAttrs.maxLength + "}$";

      reqBody.config.public.pwdComplexity.testRegExp = btoa(regExp);
      reqBody.config.public.pwdComplexity.details    = pwdComplexityAttrs;

      reqBody.config.public.pwdComplexity.details.minLength = parseInt(pwdComplexityAttrs.minLength);
      reqBody.config.public.pwdComplexity.details.maxLength = parseInt(pwdComplexityAttrs.maxLength);

      let charList = [];

      if (pwdComplexityAttrs.reqLowerCaseLetter) {
        charList.push({
          en:    "1 lowercase letter (a-z)",
          zh_HK: "1個小寫字母（a-z）",
          zh_CN: "1个小写字母（a-z）"
        });
      }

      if (pwdComplexityAttrs.reqUpperCaseLetter) {
        charList.push({
          en:    "1 uppercase letter (A-Z)",
          zh_HK: "1個大寫字母（A-Z）",
          zh_CN: "1个大写字母（A-Z）"
        });
      }

      if (pwdComplexityAttrs.reqNumber ) {
        charList.push({
          en:    "1 numerical digit (0-9)",
          zh_HK: "1個數字（0-9）",
          zh_CN: "1个数字（0-9）"
        });
      }

      if (pwdComplexityAttrs.reqSpecialChar) {
        charList.push({
          en:    "1 special character (!@#$%^&*()_+={}[]|\\:;\"'<>,.?)",
          zh_HK: "1個特殊字元 (!@#$%^&*()_+={}[]|\\:;\"'<>,.?）",
          zh_CN: "1個特殊字元（!@#$%^&*()_+={}[]|\\:;\"'<>,.?）"
        });
      }

      let hint = {
        en:    "Password must be between " + pwdComplexityAttrs.minLength + " and " +
               pwdComplexityAttrs.maxLength + " characters in length" +
               (charList.length > 0 ? ", contains at least " : "."),
        zh_HK: "密碼長度必須在 " + pwdComplexityAttrs.minLength + " 到 " +
               pwdComplexityAttrs.maxLength + " 個字符之間" +
               (charList.length > 0 ? "，包含至少" : "。"),
        zh_CN: "密码长度必须在 " + pwdComplexityAttrs.minLength + " 到 " +
               pwdComplexityAttrs.maxLength + " 个字符之间" +
               (charList.length > 0 ? "，包含至少" : "。")
      };

      for (let i=0; i<charList.length; i++) {
        let connector = {};

        if (i + 1 === charList.length) {
          connector = {
            en:    ".",
            zh_HK: "。",
            zh_CN: "。"
          };
        } else if (i + 2 === charList.length) {
          connector = {
            en:    " and ",
            zh_HK: "和",
            zh_CN: "和"
          };
        } else if (i + 2 < charList.length) {
          connector = {
            en:    ", ",
            zh_HK: "、",
            zh_CN: "、"
          };
        }

        hint.en    += (charList[i].en    + connector.en);
        hint.zh_HK += (charList[i].zh_HK + connector.zh_HK);
        hint.zh_CN += (charList[i].zh_CN + connector.zh_CN);
      }

      reqBody.config.public.pwdComplexity.hint.en    = encodeURIComponent(hint.en);
      reqBody.config.public.pwdComplexity.hint.zh_HK = encodeURIComponent(hint.zh_HK);
      reqBody.config.public.pwdComplexity.hint.zh_CN = encodeURIComponent(hint.zh_CN);

      return new Promise(async (resolve, reject) => {
        try {
          const res = await axiosAuth_PLATFORM_API.put("platform-config", reqBody);

          resolve(res.data);

          if (res.status === 200) {

            fetchPlatformConfig();

            // Restore UI to viewing mode
            setPwdComplexityEditMode(false);

            setErrMsg_editPwdComplexity('');
          }
        } catch (error) {
          if (!error.message || (error.message !== 'IGNORE')) {
            setErrMsg_editPwdComplexity(t(translateAPIError(error)));
          }
        }
      });
    }
  }


  function isNaturalNumber(str) {
    str = '' + str; // If input is a number, convert to string.
    const n = Math.floor(Number(str));
    return ((n !== Infinity) && (String(n) === str) && (n >= 0));
  }



  // ----- BEGIN UI -----

//CCCC
  return (
    <Fragment>
      <Card style={{overflow:'auto', border:'0', boxShadow:'0 2px 2px 0px rgba(0,0,0,0.1)',
                    padding:'20px 40px 20px 40px', maxWidth:'2500px', width:'100%', height:'850px',fontSize:'13px'}}>

        <div style={{display:'flex'}}>
          <span className={classes.admin_pages__page_title}>
            {t("I18N_UserMgmtConfig_Details")}
          </span>
        </div>

        {/* ----- Password Complexity ----- */}

        <div className={classes.admin_pages__page_subtitle} style={{marginTop:'20px'}}>
          <div style={{display:'flex'}}>
            <span>
              {t("I18N_Pwd_Complexity_Req")}
            </span>
            {pwdComplexityEditMode ?
              null
              :
/*
              viewPwdComplexitySect ?
*/
              <span style={{marginLeft:'15px', marginTop:'5px', fontSize:'9px', color:'SteelBlue', cursor:'pointer'}} onClick={handleEditPwdComplexity}>
                <PencilIcon size={12} />
                {t("I18N_Edit")}
              </span>
/*
              : null
*/
            }

{/*
            <span onClick={(e) => handleToggleViewSect('PASSWORD_COMPLEXITY',e)}
                  style={{ marginLeft:'auto', marginRight:'20px', marginTop:'2px', cursor: 'pointer', color: '#646464' }}>
              { viewPwdComplexitySect ? <ChevronUpIcon size={18} /> : <ChevronDownIcon  size={18} /> }
            </span>
*/}
          </div>

          <hr className="solid" style={{ margin: '0 0 0 0' }}></hr>

          {pwdComplexityEditMode ?
            <Container>
              <Form>
                <Row className={classes.admin_pages__field_label} style={{marginTop:'10px'}}>
                  <Col>{t("I18N_Password_length")}: *</Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col  style={{display:'flex', marginTop:'5px'}}>
                    <span>{t("I18N_Pwd_min_length")}: </span>
                    <span><input className={classes.admin_pages__field_editable} style={{marginLeft:'5px', width:'30px'}}
                                 name="minLength" value={pwdComplexityAttrs.minLength}
                      onChange={(e) => onObjAttrsInputChange(e)} /></span>
                    <span style={{marginLeft:'20px'}}>{t("I18N_Pwd_max_length")}: </span>
                    <span><input className={classes.admin_pages__field_editable} style={{marginLeft:'5px', width:'30px'}}
                                 name="maxLength" value={pwdComplexityAttrs.maxLength}
                      onChange={(e) => onObjAttrsInputChange(e)} /></span>
                  </Col>
                </Row>

                <Row className={classes.admin_pages__field_label} style={{marginTop:'10px'}}>
                  <span>{t("I18N_Pwd_must_include")}:</span>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col  style={{display:'flex', marginTop:'5px'}}>
                    <span>
                      <Form.Check type="switch" name={"reqLowerCaseLetter"} label={(t("I18N_Lower_case_letter"))}
                                  checked={pwdComplexityAttrs.reqLowerCaseLetter} onChange={onCheckboxSelectPwdComplexity} />
                    </span>
                    <span style={{marginLeft:'20px'}}>
                      <Form.Check type="switch" name={"reqUpperCaseLetter"} label={(t("I18N_Upper_case_letter"))}
                                  checked={pwdComplexityAttrs.reqUpperCaseLetter} onChange={onCheckboxSelectPwdComplexity} />
                    </span>
                    <span style={{marginLeft:'20px'}}>
                      <Form.Check type="switch" name={"reqNumber"}          label={(t("I18N_Number"))}
                                  checked={pwdComplexityAttrs.reqNumber}          onChange={onCheckboxSelectPwdComplexity} />
                    </span>
                    <span style={{marginLeft:'20px'}}>
                      <Form.Check type="switch" name={"reqSpecialChar"}     label={(t("I18N_Special_char"))}
                                  checked={pwdComplexityAttrs.reqSpecialChar}     onChange={onCheckboxSelectPwdComplexity} />
                    </span>
                  </Col>
                </Row>

                <Row>
                  <div display='flex' style={{marginTop:'15px'}}>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ float: 'left' }}
                      onClick={handleEditPwdComplexityCancel}>
                      {t("I18N_Cancel")}
                    </Button>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ marginLeft: '15px' }}
                      onClick={handleEditPwdComplexitySubmit}>
                      {t("I18N_Save")}
                    </Button>
                  </div>
                </Row>
              </Form>
            </Container>
            :
            <Container>
              { errMsg_editPwdComplexity === '' ?
                <>
                  <Row className={classes.admin_pages__field_label} style={{marginTop:'10px'}}>
                    <Col>{t("I18N_Password_length")}: *</Col>
                  </Row>
                  <Row className={classes.admin_pages__field_value}>
                    <Col style={{display:'flex', marginTop:'5px'}}>
                      <span>{t("I18N_Pwd_min_length")}: </span>
                      <span style={{marginLeft:'5px'}}>{pwdComplexityAttrs.minLength}</span>
                      <span style={{marginLeft:'20px'}}>{t("I18N_Pwd_max_length")}: </span>
                      <span style={{marginLeft:'5px'}}>{pwdComplexityAttrs.maxLength}</span>
                    </Col>
                  </Row>

                  <Row className={classes.admin_pages__field_label} style={{marginTop:'10px'}}>
                    <span>{t("I18N_Pwd_must_include")}:</span>
                  </Row>
                  <Row className={classes.admin_pages__field_value}>
                    <Col  style={{display:'flex', marginTop:'5px'}}>
                      <span>
                      { (pwdComplexityAttrs && pwdComplexityAttrs.reqLowerCaseLetter) ?
                        <span><BiCheckSquare size={16} style={{marginTop:'-3px', marginRight:'2px'}}/></span>
                      :
                        <span><BiRectangle size={16} style={{marginTop:'-3px', marginRight:'2px'}}/></span>
                      }
                      </span>
                      <span style={{marginLeft:'5px'}}>{t("I18N_Lower_case_letter")}</span>

                      <span style={{marginLeft:'20px'}}>
                      { (pwdComplexityAttrs && pwdComplexityAttrs.reqUpperCaseLetter) ?
                        <span><BiCheckSquare size={16} style={{marginTop:'-3px', marginRight:'2px'}}/></span>
                      :
                        <span><BiRectangle size={16} style={{marginTop:'-3px', marginRight:'2px'}}/></span>
                      }
                      </span>
                      <span style={{marginLeft:'5px'}}>{t("I18N_Upper_case_letter")}</span>

                      <span style={{marginLeft:'20px'}}>
                      { (pwdComplexityAttrs && pwdComplexityAttrs.reqNumber) ?
                        <span><BiCheckSquare size={16} style={{marginTop:'-3px', marginRight:'2px'}}/></span>
                      :
                        <span><BiRectangle size={16} style={{marginTop:'-3px', marginRight:'2px'}}/></span>
                      }
                      </span>
                      <span style={{marginLeft:'5px'}}>{t("I18N_Number")}</span>

                      <span style={{marginLeft:'20px'}}>
                      { (pwdComplexityAttrs && pwdComplexityAttrs.reqSpecialChar) ?
                        <span><BiCheckSquare size={16} style={{marginTop:'-3px', marginRight:'2px'}}/></span>
                      :
                        <span><BiRectangle size={16} style={{marginTop:'-3px', marginRight:'2px'}}/></span>
                      }
                      </span>
                      <span style={{marginLeft:'5px'}}>{t("I18N_Special_char")}</span>

                    </Col>
                  </Row>

                  <Row className={classes.admin_pages__field_label} style={{ marginTop: '20px', width:'100%' }}>
                    <Col>{t("I18N_Created_by")}:</Col>
                    <Col>{t("I18N_Time_created")}:</Col>
                    <Col>{t("I18N_Updated_by")}:</Col>
                    <Col>{t("I18N_Time_updated")}:</Col>
                  </Row>
                  <Row className={classes.admin_pages__field_value} style={{ marginTop: '0px', width:'100%' }}>
                    <Col style={{overflowWrap:'anywhere'}}>{platformConfig.creatorUsername}</Col>
                    <Col>{formatDate(platformConfig.timeCreated)}</Col>
                    <Col style={{overflowWrap:'anywhere'}}>{platformConfig.updaterUsername}</Col>
                    <Col>{formatDate(platformConfig.timeUpdated)}</Col>
                  </Row>
                </>
              : null
              }
            </Container>
          }

          <div className={classes.admin_pages__field_label} style={{ marginTop:'5px', color:'rgba(200,0,0)' }}>
            {errMsg_editPwdComplexity}
          </div>

        </div>

      </Card>
    </Fragment>
  )
}

export default UserMgmtConfigAdmin;
